import { useState } from "react";
import { useParams } from "react-router";
import { Plus, X, AlertTriangle } from "lucide-react";
import { EnterprisePageShell } from "./EnterprisePageShell";
import { SsoConfigCard } from "../../../components/shared/SsoConfigCard";
import {
  usePlatformEnterprise, useUpdatePlatformEnterpriseSettings,
  useSuspendEnterprise, useReinstateEnterprise,
  useEnterpriseStorage, useUpdateEnterpriseStorage,
} from "@platform/hooks";
import { useToast } from "@nthamizh/ui";
// Alias so the sub-component can use its own toast instance
const useToastStorage = useToast;

interface Enterprise {
  id: string; name: string; slug: string; status: string; allowedDomains: string[];
}

export function PlatformEnterpriseSettingsPage() {
  const { id: enterpriseId } = useParams<{ id: string }>();
  const { data: enterprise } = usePlatformEnterprise(enterpriseId ?? null);
  const updateSettings = useUpdatePlatformEnterpriseSettings();
  const suspend = useSuspendEnterprise();
  const reinstate = useReinstateEnterprise();
  const { toast } = useToast();

  const ent = enterprise as Enterprise | undefined;

  const [name, setName] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[] | null>(null);
  const [newDomain, setNewDomain] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [synced, setSynced] = useState(false);

  if (ent && !synced) {
    setName(ent.name);
    setDomains(ent.allowedDomains ?? []);
    setSynced(true);
  }

  const currentName = name ?? ent?.name ?? "";
  const currentDomains = domains ?? ent?.allowedDomains ?? [];

  const handleSaveProfile = async () => {
    if (!enterpriseId) return;
    try {
      await updateSettings.mutateAsync({ id: enterpriseId, name: currentName });
      toast({ title: "Enterprise profile saved", variant: "success" });
    } catch { toast({ title: "Failed to save", variant: "error" }); }
  };

  const handleSaveDomains = async () => {
    if (!enterpriseId) return;
    try {
      await updateSettings.mutateAsync({ id: enterpriseId, allowedDomains: currentDomains });
      toast({ title: "Domain settings saved", variant: "success" });
    } catch { toast({ title: "Failed to save domains", variant: "error" }); }
  };

  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!d || currentDomains.includes(d)) return;
    setDomains([...currentDomains, d]);
    setNewDomain("");
  };

  const removeDomain = (d: string) => setDomains(currentDomains.filter((x) => x !== d));

  const handleSuspend = async () => {
    if (!ent) return;
    try {
      await suspend.mutateAsync(ent.id);
      toast({ title: `${ent.name} suspended`, variant: "success" });
    } catch { toast({ title: "Failed to suspend", variant: "error" }); }
  };

  const handleReinstate = async () => {
    if (!ent) return;
    try {
      await reinstate.mutateAsync(ent.id);
      toast({ title: `${ent.name} reinstated`, variant: "success" });
    } catch { toast({ title: "Failed to reinstate", variant: "error" }); }
  };

  return (
    <EnterprisePageShell enterpriseId={enterpriseId ?? ""} pageName="Settings">
      <div className="grid grid-cols-2 gap-5">
        {/* Enterprise profile */}
        <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
          <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-4">Enterprise profile</p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Enterprise name</label>
              <input
                value={currentName}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Subdomain</label>
              <input
                value={ent?.slug ?? ""}
                disabled
                className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg bg-ds-bg opacity-60 cursor-not-allowed"
              />
              <p className="text-xs text-ds-text-muted mt-1">Subdomain cannot be changed after creation.</p>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={handleSaveProfile} disabled={updateSettings.isPending}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {updateSettings.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {/* Auto-approval domains */}
        <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
          <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-1">Auto-approval domains</p>
          <p className="text-xs text-ds-text-muted mb-4">
            Users with these email domains are automatically approved on registration. Leave empty to require manual approval.
          </p>
          <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
            {currentDomains.length === 0 && (
              <span className="text-xs text-ds-text-muted italic">No domains configured — manual approval required.</span>
            )}
            {currentDomains.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-xs text-indigo-700 font-medium">
                @{d}
                <button onClick={() => removeDomain(d)} className="text-indigo-400 hover:text-indigo-700">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 mb-4">
            <div className="flex items-center flex-1 border border-ds-border rounded-lg overflow-hidden">
              <span className="px-2.5 text-sm text-ds-text-muted">@</span>
              <input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDomain()}
                placeholder="domain.com"
                className="flex-1 py-2 pr-3 text-sm focus:outline-none bg-transparent"
              />
            </div>
            <button onClick={addDomain}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-medium">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <div className="flex justify-end">
            <button onClick={handleSaveDomains} disabled={updateSettings.isPending}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              Save domains
            </button>
          </div>
        </div>

        {/* SSO */}
        <div className="col-span-2">
          <SsoConfigCard
            enterpriseId={enterpriseId ?? ""}
            enterpriseSlug={ent?.slug ?? ""}
          />
        </div>

        {/* Suspend / Reinstate + Danger zone — full width */}
        <div className="col-span-2 bg-white border border-red-200 rounded-card shadow-card p-5">
          <h2 className="text-xs font-semibold text-red-600 uppercase tracking-wide flex items-center gap-1.5 mb-4">
            <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
          </h2>

          {/* Suspend / reinstate */}
          <div className="flex items-start justify-between gap-4 pb-4 border-b border-ds-border">
            <div>
              <p className="text-sm font-medium text-ds-text">
                {ent?.status === "active" ? "Suspend enterprise" : "Reinstate enterprise"}
              </p>
              <p className="text-xs text-ds-text-muted mt-0.5">
                {ent?.status === "active"
                  ? "Users will lose access immediately. Enterprise data is preserved."
                  : "Restores access for all enterprise users."}
              </p>
            </div>
            {ent?.status === "active" ? (
              <button onClick={handleSuspend} disabled={suspend.isPending}
                className="flex-shrink-0 px-3.5 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 text-sm font-medium disabled:opacity-50">
                Suspend enterprise
              </button>
            ) : (
              <button onClick={handleReinstate} disabled={reinstate.isPending}
                className="flex-shrink-0 px-3.5 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-sm font-medium disabled:opacity-50">
                Reinstate enterprise
              </button>
            )}
          </div>

          {/* Reset */}
          <div className="flex items-start justify-between gap-4 pt-4">
            <div>
              <p className="text-sm font-medium text-ds-text">Reset enterprise</p>
              <p className="text-xs text-ds-text-muted mt-0.5">
                Removes all users, roles, and configuration. Cannot be undone.
              </p>
            </div>
            <button onClick={() => setShowConfirm(true)}
              className="flex-shrink-0 px-3.5 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-sm font-medium">
              Reset enterprise
            </button>
          </div>

          {showConfirm && (
            <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50 space-y-3">
              <p className="text-sm text-red-700">Type <strong>RESET</strong> to confirm. This cannot be undone.</p>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type RESET"
                className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:outline-none focus:border-red-500 bg-white" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                  className="px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">
                  Cancel
                </button>
                <button disabled={confirmText !== "RESET"}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700">
                  Confirm reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Storage limits — platform admin only; the route itself is only
          reachable by platform admins, so no role check needed here. */}
      {ent && <EnterpriseStoragePanel enterpriseId={ent.id} />}
    </EnterprisePageShell>
  );
}
/** Storage limits + usage bar — platform admin only, separate component
 *  so the parent doesn't grow. */
function EnterpriseStoragePanel({ enterpriseId }: { enterpriseId: string }) {
  const { data, isLoading } = useEnterpriseStorage(enterpriseId);
  const updateStorage = useUpdateEnterpriseStorage();
  const { toast } = useToastStorage();

  const [perUserMb, setPerUserMb] = useState("");
  const [totalGb, setTotalGb] = useState("");
  const [synced, setSynced] = useState(false);

  if (data && !synced) {
    setPerUserMb(data.perUserLimitBytes ? String(Math.round(data.perUserLimitBytes / (1024 * 1024))) : "");
    setTotalGb(data.totalLimitBytes ? String(Math.round(data.totalLimitBytes / (1024 * 1024 * 1024))) : "");
    setSynced(true);
  }

  const usedGb = data ? (data.usedBytes / (1024 ** 3)).toFixed(2) : "—";
  const totalLimitGb = data?.totalLimitBytes ? (data.totalLimitBytes / (1024 ** 3)).toFixed(1) : null;
  const usagePct = data?.totalLimitBytes ? Math.min(100, (data.usedBytes / data.totalLimitBytes) * 100) : 0;

  const handleSave = async () => {
    try {
      await updateStorage.mutateAsync({
        id: enterpriseId,
        userStorageLimitBytes: perUserMb ? Number(perUserMb) * 1024 * 1024 : null,
        totalStorageLimitBytes: totalGb ? Number(totalGb) * 1024 * 1024 * 1024 : null,
      });
      toast({ title: "Storage limits saved", variant: "success" });
    } catch {
      toast({ title: "Failed to save storage limits", variant: "error" });
    }
  };

  if (isLoading) return <p className="text-sm text-ds-text-muted">Loading storage…</p>;

  return (
    <div className="bg-white border border-ds-border rounded-card p-5 space-y-4">
      <h3 className="text-sm font-semibold text-ds-text">Storage limits</h3>

      {data?.totalLimitBytes && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-ds-text-secondary">Enterprise total used</p>
            <p className="text-xs text-ds-text-muted">{usedGb} GB / {totalLimitGb} GB</p>
          </div>
          <div className="h-2.5 bg-ds-bg rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${usagePct >= 90 ? "bg-red-500" : usagePct >= 70 ? "bg-amber-400" : "bg-indigo-500"}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
          <p className="text-xs text-ds-text-muted mt-1">{usagePct.toFixed(1)}% used</p>
        </div>
      )}

      {!data?.totalLimitBytes && (
        <p className="text-xs text-ds-text-muted">No total enterprise cap set — set one below to track usage against it.</p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-ds-text">Per-user limit (MB)</label>
          <input
            type="number" min={1} value={perUserMb}
            onChange={(e) => setPerUserMb(e.target.value)}
            placeholder="e.g. 500 (blank = platform default)"
            className="w-full h-9 rounded-lg border border-ds-border px-3 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-ds-text">Enterprise total (GB)</label>
          <input
            type="number" min={1} value={totalGb}
            onChange={(e) => setTotalGb(e.target.value)}
            placeholder="e.g. 20 (blank = unlimited)"
            className="w-full h-9 rounded-lg border border-ds-border px-3 text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={updateStorage.isPending}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
        >
          Save limits
        </button>
      </div>
    </div>
  );
}
